import { Subject, takeUntil } from 'rxjs';
import { setTimeoutWithDestroyRef } from 'src/app/core/utils/lifecycle-timeout';
import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy , DestroyRef, inject} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ConstantConfig } from 'src/app/core/config/constant.config';
import { MsgConstantConfig } from 'src/app/core/config/message.constant.config';
import { BroadcastService } from 'src/app/core/services/broadcaster.service';
import { LoaderService } from 'src/app/core/services/loader.service';

@Component({
  selector: 'app-loading-bar-modal',
  templateUrl: './loading-bar-modal.component.html',
  styleUrls: ['./loading-bar-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class LoadingBarModalComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly destroyRef = inject(DestroyRef);


  // messages that are displayed on the pop up
  @Input() message = '';
  @Input() message2 = '';
  //if time_bar gets true bar progress moves slowly
  @Input() time_bar?: any = false;
  //for stuck at 90 until process not complete
  @Input() slowSpeed?: number = 5000;
  percentage: number = 0;
  //if this pop up need to be draggable set this to true
  @Input() draggable: boolean = false;

  @Input() message3 = '';
  @Input() config: any = {};
  isProgressZero: boolean = false;

  // enable dynamic processing messages (OPTIONAL)
  @Input() enableDynamicMessages: boolean = false;

  // message interval (default 20 sec)
  @Input() messageInterval: number = 20000;

  // duration for normal messages (default 200 sec)
  @Input() normalMessageDuration: number = 200000;
  @Input() options: any;

  processingMessages: string[] = [
    'Processing your request',
    'Please wait while we complete the operation',
    'Working on your request',
    'This action is currently being processed',
    'Handling the request securely',
    'Performing required checks',
    'Communicating with required systems',
    'Updating records',
    'Applying changes',
    'Finalizing the operation'
  ];

  extendedProcessingMessages: string[] = [
    'The request is still being processed',
    'Processing is ongoing. Please keep this window open'
  ];

  private messageTimer: any;
  private messageIndex: number = 0;
  private elapsedTime: number = 0;
  private loaderPercentage:any;

  constructor(
    public activeModal: NgbActiveModal,
    public loaderService: LoaderService,
    private broadCastService: BroadcastService,
    private translateService: TranslateService
  ) {
    this.translateService.setDefaultLang('en');
    this.translateService.use(localStorage.getItem('Selected_Language') || 'en');
  }

  ngOnInit(): void {
    this.broadCastService
      .on(MsgConstantConfig.SHOW_HIDE_CLIENT_PROGRESS_BAR).pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res) {
          const key = this.storageKey;
          if (key) {
            localStorage.removeItem(this.storageKey);
          }
          this.percentage = 100;
        }
      });

    this.broadCastService
      .on(MsgConstantConfig.RESET_LOADING_BAR_PROGRESS).pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res && !this.isProgressZero) {
          this.percentage = 0;
          this.isProgressZero = true;
        }
      });

    // OPTIONAL dynamic message flow
    if (this.enableDynamicMessages) {
      const key = this.storageKey;
      if (key) {
        const savedState = localStorage.getItem(this.storageKey);
        if (savedState) {
          const state = JSON.parse(savedState);
          this.percentage = state.percentage || 0;
          this.messageIndex = state.messageIndex || 0;
          this.elapsedTime = state.elapsedTime || 0;
          this.message2 = state.message2 || this.message2;
        }
      }
      this.startDynamicMessageFlow();
    }
    this.timer(this.time_bar);

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.messageTimer) {
      clearInterval(this.messageTimer);
      clearTimeout(this.loaderPercentage);
    }
  }

  // this determines if the bar will increase rapidly or slowly
  timer(time_b: any) {

    // slower bar progress
    if (time_b) {
      // this.loaderService.hideLoader();
      this.loaderPercentage = setTimeoutWithDestroyRef(
        this.destroyRef, () => this.increaseLoaderPercentage(this.percentage),
        this.getDynamicSpeed()
      );
    }

    // rapid bar progress
    if (!time_b) {
      // this.loaderService.hideLoader();
      this.loaderPercentage = setTimeoutWithDestroyRef(
        this.destroyRef, () => this.increaseLoaderPercentage(this.percentage),
        1000
      );
    }
  }

  // increases the bar progress
  increaseLoaderPercentage(item: number) {
    if (this.percentage < 99) {
      this.percentage += 1;
      this.options?.loanId && this.saveState();
      return this.timer(this.time_bar);
    }
  }

  // dynamically slows loader near 90+ (uses existing slowSpeed)
  getDynamicSpeed() {
    const baseInterval =
      (this.options?.transaction_in_bg_progress_approximate_time * 1000) / 100;

    return baseInterval || this.slowSpeed;
  }

  //dynamic msg flow
  startDynamicMessageFlow(): void {

    if (!this.message2) {
      this.message2 = this.processingMessages[this.messageIndex] || '';
      this.messageIndex++;
    }

    this.messageTimer = setInterval(() => {

      this.elapsedTime += this.messageInterval;

      // normal messages
      if (this.elapsedTime <= this.normalMessageDuration) {
        if (this.messageIndex < this.processingMessages.length) {
          this.message2 = this.processingMessages[this.messageIndex];
          this.messageIndex++;
        }
      }
      // extended-duration messages (looped)
      else {
        const idx =
          (this.messageIndex - this.processingMessages.length) %
          this.extendedProcessingMessages.length;

        this.message2 = this.extendedProcessingMessages[idx];
        this.messageIndex++;
      }

      this.options?.loanId && this.saveState();

    }, this.messageInterval);
  }


  private get storageKey(): string | null {
    if (!this.options?.loanId) {
      return null;
    }
    return `${this.options?.loanId}_${ConstantConfig.LOADER_STATE}`;
  }

  private saveState(): void {
    const state = {
      percentage: this.percentage,
      messageIndex: this.messageIndex,
      elapsedTime: this.elapsedTime,
      message2: this.message2
    };
    const key = this.storageKey;
    if (key) {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    }
  }

}
